/* eslint-disable import/extensions */
import React, { useState, useEffect, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import styled, { css } from 'styled-components';
import { Button } from 'components';
import { motion } from 'framer-motion';
import Dropzone from 'react-dropzone';
import mime from 'mime';
import { CircularProgress } from '@mui/material';
import { hexToRGB, WebWorker, slugify } from 'utils';
import imageCompression from 'browser-image-compression';
import zipFilesWorkerScript from 'utils/zipFiles.worker.js';
import { LocalContext } from 'context';
import { defaultColors } from 'styles';
import crossmark from 'assets/svgs/crossmark.svg';
import checkmark from 'assets/svgs/checkmark.svg';
import { UploadIcon } from 'assets/svgs';

const getFormInitialState = () => ({
  name: '',
  email: '',
  contactNumber: '',
  fullSiteAddress: '',
  eircode: '',
  additionalNotes: ''
});

const Form = React.forwardRef((props, ref) => {
  const { type } = props;
  const [formValues, setFormValues] = useState(getFormInitialState());
  const [filesToSend, setFilesToSend] = useState([]);
  const [zipFilesWorker, setZipFilesWorker] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [changeDropZoneBackgroundColor, setChangeDropZoneBackgroundColor] = useState(false);
  const { quote, setQuote, setCartCounter } = useContext(LocalContext);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalUploadedFileSize, setTotalUploadedFileSize] = useState(0);
  const [compressingImages, setCompressingImages] = useState(false);

  const encodeData = (_data) => {
    const formData = new FormData();

    Object.keys(_data).forEach((key) => formData.append(key, _data[key]));

    return formData;
  };

  const formatQuote = (_quote) => (
    <ul>
      {_quote.map((item) => (
        <li key={item.productId}>
          <p style={{ margin: 0 }}>
            <b>Product Title: </b>
            {item.title}
          </p>
          <p style={{ margin: 0 }}>
            <b>Product ID: </b>
            {item.productId}
          </p>
          <p style={{ margin: 0 }}>
            <b>Dimensions: </b>
            {item.dimensions.label}
          </p>
          <p style={{ margin: 0 }}>
            <b>Quantity: </b>
            {item.quantity}
          </p>
          <br />
        </li>
      ))}
    </ul>
  );

  const sendData = ({ formValues: _formValues, files }) => {
    try {
      const quoteRequested =
        type === 'Request A Quote' && quote.length
          ? JSON.stringify(renderToStaticMarkup(formatQuote(quote)))
          : '';

      const url = '/';

      const formName = document.forms[0].getAttribute('name');

      const options = {
        method: 'POST',
        body: encodeData({
          'form-name': formName,
          name: _formValues.name,
          email: _formValues.email,
          contactNumber: _formValues.contactNumber,
          fullSiteAddress: _formValues.fullSiteAddress,
          eircode: _formValues.eircode,
          additionalNotes: _formValues.additionalNotes,
          files: files ?? 'No Files Attatched',
          quoteRequested
        })
      };

      return fetch(url, options);
    } catch (error) {
      console.error(error);
      setErrorMessage(error);
    }
  };

  useEffect(() => {
    const _zipFilesWorker = new WebWorker(zipFilesWorkerScript);

    _zipFilesWorker.onmessage = async (event) => {
      const { data } = event;
      if (data) {
        await sendData({ formValues: data.formValues, files: data.zippedFiles });
        setMessageSent(true);
        setSendingMessage(false);
        setFormValues(getFormInitialState());
        setFilesToSend([]);
      }
    };

    setZipFilesWorker(_zipFilesWorker);

    return () => {
      _zipFilesWorker.terminate();
    };
  }, []);

  useEffect(() => {
    const _totalUploadedFileSize = filesToSend.reduce((acc, file) => {
      // eslint-disable-next-line no-param-reassign
      acc += file.size;
      return acc;
    }, 0);

    setTotalUploadedFileSize(_totalUploadedFileSize);
  }, [filesToSend]);

  const handleInputChange = (e) => {
    e.persist();

    if (errorMessage) {
      setErrorMessage('');
    }

    const { name, value } = e.target;

    setFormValues((currentState) => ({
      ...currentState,
      [name]: value
    }));
  };

  const normaliseFile = ({ file, index = 0 }) => {
    // Try the browser-supplied extension first
    let ext = (file.name && /\.[^.]+$/.exec(file.name)?.[0]) || '';

    // Fallback: derive from MIME type
    if (!ext) ext = `.${mime.getExtension(file.type) || 'bin'}`;

    return new File([file], `${index + 1}${ext}`, {
      type: file.type,
      lastModified: file.lastModified
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSendingMessage(true);
      if (filesToSend.length > 1 && zipFilesWorker) {
        const nameSlug = slugify(formValues.name);
        const normalisedFiles = filesToSend.map((file, index) => normaliseFile({ file, index }));
        zipFilesWorker.postMessage({ formValues, filesToSend: normalisedFiles, nameSlug });
      } else {
        const oneFile = filesToSend[0];
        await sendData({ formValues, files: oneFile ? normaliseFile({ file: oneFile }) : '' });
        setMessageSent(true);
        setSendingMessage(false);
        setFormValues(getFormInitialState());
        setFilesToSend([]);
        if (type === 'Request A Quote') {
          setQuote([]);
          setCartCounter(0);
        }
      }
    } catch (error) {
      console.error(error);
      setMessageSent(false);
      setSendingMessage(false);
    }
  };

  const handleDragEvent = () => {
    setChangeDropZoneBackgroundColor((currentValue) => !currentValue);
  };

  const compressImage = async (imageFile) => {
    console.log(`Original image size: ${imageFile.size / 1024 / 1024} MB`);

    const options = {
      useWebWorker: true
    };

    try {
      const compressedFile = await imageCompression(imageFile, options);
      console.log(`Compressed image size:  ${compressedFile.size / 1024 / 1024} MB`);
      return compressedFile;
    } catch (error) {
      console.error(error);
    }
  };

  const handleDrop = async (droppedFiles) => {
    if (compressingImages) {
      return;
    }

    setChangeDropZoneBackgroundColor(false);

    const imagesToCompress = [];
    const otherFiles = [];

    droppedFiles.forEach(async (file) => {
      if (file.type.includes('image')) {
        imagesToCompress.push(compressImage(file));
      } else {
        otherFiles.push(file);
      }
    });

    let compressedImages = [];

    if (imagesToCompress.length > 0) {
      setCompressingImages(true);
      compressedImages = await Promise.all(imagesToCompress);
      setCompressingImages(false);
    }

    const newFilesToSend = [...compressedImages, ...otherFiles];

    setFilesToSend((_filesToSend) => [..._filesToSend, ...newFilesToSend]);
  };

  const removeFile = (fileToBeRemoved) => {
    setFilesToSend((_filesToSend) => _filesToSend.filter((file) => file !== fileToBeRemoved));
  };

  return (
    <Container className={props.className} ref={ref}>
      <h4>Send Us Your Requirements</h4>
      <form
        name={type}
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={(e) => handleSubmit(e)}>
        <label>
          <p>
            Full Name<span>*</span>
          </p>
          <input
            type="text"
            name="name"
            onChange={handleInputChange}
            value={formValues.name}
            placeholder="Enter Full Name"
            required
          />
        </label>
        <label>
          <p>
            Email<span>*</span>
          </p>
          <input
            type="email"
            name="email"
            onChange={handleInputChange}
            value={formValues.email}
            placeholder="Enter Email"
            required
          />
        </label>
        <label>
          <p>
            Contact Number<span>*</span>
          </p>
          <input
            type="tel"
            name="contactNumber"
            onChange={handleInputChange}
            value={formValues.contactNumber}
            placeholder="Enter Contact Number"
            required
          />
        </label>
        <label>
          <p>
            Full Site Address<span>*</span>
          </p>
          <input
            type="text"
            name="fullSiteAddress"
            onChange={handleInputChange}
            value={formValues.fullSiteAddress}
            placeholder="Enter Full Site Address"
            required
          />
        </label>
        <label>
          <p>
            Eircode<span>*</span>
          </p>
          <input
            type="text"
            name="eircode"
            onChange={handleInputChange}
            value={formValues.eircode}
            placeholder="Enter Eircode"
            required
          />
        </label>
        <label>
          <p>
            Additional Notes<span>*</span>
          </p>
          <textarea
            name="additionalNotes"
            onChange={handleInputChange}
            value={formValues.additionalNotes}
            placeholder="Enter Your Message"
            rows={5}
            required
          />
        </label>
        {type === 'Request A Quote' && <input type="hidden" name="quoteRequested" />}
        <label>
          <p>Upload Photos and/or Plans</p>
          <DropzoneWrapper
            changeDropZoneBackgroundColor={changeDropZoneBackgroundColor}
            disabled={compressingImages}>
            <Dropzone
              multiple
              onDrop={handleDrop}
              onDragEnter={handleDragEvent}
              onDragLeave={handleDragEvent}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <motion.div
                    whileTap={{ scale: compressingImages ? 1 : 0.95 }}
                    {...getRootProps()}
                    className="dropzone-area"
                    onClick={(e) => e.stopPropagation()}>
                    <input {...getInputProps()} name="files" disabled={compressingImages} />
                    {compressingImages ? (
                      <CircularProgress style={{ color: defaultColors.primary }} size="3rem" />
                    ) : (
                      <>
                        <StyledUploadIcon />
                        <p style={{ pointerEvents: 'none' }}>
                          <span style={{ fontWeight: 600 }}>Drag & drop</span> or{' '}
                          <span style={{ fontWeight: 600, textDecoration: 'underline' }}>
                            click
                          </span>{' '}
                          to browse files
                        </p>
                        <small>You can upload up to 7mb maximum</small>
                      </>
                    )}
                  </motion.div>
                </section>
              )}
            </Dropzone>
          </DropzoneWrapper>
        </label>
        {filesToSend.length > 0 && (
          <>
            <FileNames>
              Files:
              {filesToSend.map((file, i) => (
                <FileName key={i} onClick={() => removeFile(file)}>
                  {file.name}
                  {totalUploadedFileSize > 7000000 ? (
                    <span>{` (Size: ${(file.size / 1000000).toFixed(2)}mb)`}</span>
                  ) : (
                    ''
                  )}
                  <img src={checkmark} alt="checkmark" />
                  <img src={crossmark} alt="crossmark" />
                </FileName>
              ))}
            </FileNames>
            {totalUploadedFileSize > 7000000 && (
              <TotalFileSize>
                Total Uploaded File Size: {(totalUploadedFileSize / 1000000).toFixed(2)}mb
              </TotalFileSize>
            )}
          </>
        )}
        {totalUploadedFileSize > 7000000 ? (
          <TotalFileSizeWarning>
            Total uploaded file size cannot exceed 7mb. Please upload smaller files or delete some
            of the files listed above.
            <br />
            <br />
            Alternatively, you can email us files directly at{' '}
            <a href="mailto:info@connollystairs.ie" style={{ color: 'var(--connolly-red)' }}>
              info@connollystairs.ie
            </a>
          </TotalFileSizeWarning>
        ) : messageSent ? (
          <h4>Message Sent!</h4>
        ) : (
          <Button
            type="submit"
            loading={sendingMessage}
            loadingButton
            disabled={compressingImages}
            shine={!sendingMessage && !compressingImages}
            shineDuration={3}>
            {type === 'Contact Us' ? 'Send Message' : 'Request A Quote'}
          </Button>
        )}
      </form>
    </Container>
  );
});

const TotalFileSize = styled.p`
  font-weight: 700;
  margin: 1rem auto 0.5rem;
  text-align: center;
`;

const TotalFileSizeWarning = styled.p`
  color: var(--connolly-red);
  font-weight: 700;

  /* margin-top: 1rem; */
  text-align: center;
`;

const StyledUploadIcon = styled(UploadIcon)`
  margin-bottom: 0.75rem;
  width: 30px;
`;

const DropzoneWrapper = styled.div`
  .dropzone-area {
    align-items: center;
    border-radius: 6px;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: flex;
    flex-direction: column;
    height: 125px;
    justify-content: center;
    outline: none;
    padding: 0 1rem;
    transition:
      background-color 150ms ease-in-out,
      border 150ms ease-in-out;

    small {
      margin-top: 0.063em;
    }

    svg,
    p,
    small {
      position: relative;
      text-align: center;
      z-index: -1;
    }

    ${({ changeDropZoneBackgroundColor }) =>
      changeDropZoneBackgroundColor
        ? css`
            background-color: ${hexToRGB({ color: '#FF3E45', alpha: 0.3 })};
            border: 0.063rem solid var(--connolly-red);
          `
        : css`
            background-color: transparent;
            border: 0.063rem solid #c4c4c4;
          `};
  }
`;

const FileNames = styled.div`
  align-items: flex-start;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0.125em 0 0.063em;
  padding-left: 0.5em;
`;

const FileName = styled.span`
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 2em;
  position: relative;
  text-align: left;
  transition: color 100ms ease-in-out;

  img {
    position: absolute;
    transition: opacity 100ms ease-in-out;
  }

  img[alt='checkmark'] {
    top: 5px;
    right: -20px;
  }

  img[alt='crossmark'] {
    top: 6px;
    right: -18px;
  }

  &:hover {
    color: #000;

    img[alt='checkmark'] {
      opacity: 0;
    }

    img[alt='crossmark'] {
      opacity: 1;
    }
  }

  &:not(:hover) {
    color: var(--connolly-red);

    img[alt='checkmark'] {
      opacity: 1;
    }

    img[alt='crossmark'] {
      opacity: 0;
    }
  }
`;

const Container = styled.div`
  h4 {
    font-size: 1.125rem;
    margin: 1.175em 0 1.2em;
    text-align: center;

    @media (width >= 1200px) {
      text-align: left;
    }
  }

  form {
    width: 100%;

    input,
    textarea {
      border-radius: 6px;
      border: 1px solid #c4c4c4;
      display: block;
      font-size: 1rem;
      margin-bottom: 0.75em;
      margin-top: 0.4em;
      outline: none;
      padding: 0.625em;
      width: 100%;
    }

    label:last-of-type > div {
      font-size: 1rem;
      margin-bottom: 0.65em;
      margin-top: 0.4em;
    }

    input {
      height: 40px;
    }

    textarea {
      height: 125px;
    }

    label {
      color: var(--connolly-black);

      > p {
        font-weight: 700;
        padding-left: 0.5em;

        > span {
          color: var(--connolly-red);
        }
      }
    }

    > button {
      display: block;
      margin: 20px auto 0;

      @media (width >= 1200px) {
        margin: 20px 0 0;
      }
    }
  }
`;

export default Form;
